import { Offcanvas, Form, Button, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import React, { useEffect, useState } from "react";
import "react-phone-input-2/lib/bootstrap.css";
import PhoneInput from "react-phone-input-2";
import { Box, Divider, Typography } from "@mui/material";
import {
  PersonPlusFill,
  ShieldLockFill,
  ImageFill,
} from "react-bootstrap-icons";
import API_BASE_URL from "../../../config/api";

const SectionHeader = ({ icon, label }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2} mt={2}>
    <Box sx={{ color: "#0d47a1", display: "flex" }}>{icon}</Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#7b8ab8",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#dde4f5" }} />
  </Box>
);

const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const AddUserOffcanvas = ({ show, handleClose, onUserAdded }) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      location: "",
      number: "",
      password: "",
      confirmPassword: "",
      dateofbirth: "",
      files: null,
    },
  });

  const [locations, setLocations] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/location/admin/getAllLocations`)
      .then((res) =>
        setLocations(
          (res.data?.data || []).filter((l) => l.status === "Active"),
        ),
      )
      .catch(() => toast.error("Failed to load locations"));
  }, []);

  const formatDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${day}-${m}-${y}`;
  };

  const onSubmit = async (data) => {
    const token = localStorage.getItem("adminToken");
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (k === "dateofbirth") fd.append(k, formatDate(v));
      else if (k === "files") {
        if (v?.[0]) fd.append("files", v[0]);
      } else fd.append(k, v);
    });
    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/adminRegister/user`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      toast.success("User added successfully!");
      onUserAdded();
      handleClose();
      reset();
    } catch (err) {
      toast.error(
        "Failed to add user: " + (err.response?.data?.message || err.message),
      );
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      style={{ width: 420 }}
    >
      <Offcanvas.Header
        closeButton
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #1a237e, #0d47a1)",
          borderBottom: "none",
        }}
      >
        <Offcanvas.Title
          className="d-flex align-items-center gap-2 fw-bold"
          style={{ fontSize: "1rem" }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: "rgba(255,235,59,0.2)",
              border: "1.5px solid rgba(255,235,59,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonPlusFill size={13} color="#ffeb3b" />
          </Box>
          Add New User
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ background: "#f0f4ff" }}>
        <Form
          onSubmit={handleSubmit(onSubmit)}
          className="p-3 rounded-3 shadow-sm bg-white"
        >
          <SectionHeader
            icon={<PersonPlusFill size={13} />}
            label="Personal Info"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Full Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              style={fieldStyle}
              placeholder="Enter full name"
              {...register("name", { required: "Name is required" })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Email <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              style={fieldStyle}
              type="email"
              placeholder="Enter email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email",
                },
              })}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Location <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              style={fieldStyle}
              {...register("location", { required: "Location is required" })}
              isInvalid={!!errors.location}
            >
              <option value="">Select location</option>
              {locations.map((l) => (
                <option key={l._id} value={l._id}>
                  {l.location}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.location?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Phone Number</Form.Label>
            <PhoneInput
              country="no"
              enableSearch
              specialLabel=""
              inputStyle={{ ...fieldStyle, width: "100%" }}
              value={watch("number")}
              onChange={(phone) => {
                setValue("number", phone);
                if (errors.number) clearErrors("number");
              }}
            />
            {errors.number && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.number.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Date of Birth</Form.Label>
            <Form.Control
              style={fieldStyle}
              type="date"
              max={new Date().toISOString().split("T")[0]}
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              {...register("dateofbirth", {
                validate: {
                  notFuture: (v) =>
                    !v ||
                    new Date(v) <= new Date() ||
                    "Cannot be in the future",
                },
              })}
              isInvalid={!!errors.dateofbirth}
            />
            <Form.Control.Feedback type="invalid">
              {errors.dateofbirth?.message}
            </Form.Control.Feedback>
            <Form.Text muted>Stored as DD-MM-YYYY</Form.Text>
          </Form.Group>

          <SectionHeader icon={<ShieldLockFill size={13} />} label="Security" />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Password <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              style={fieldStyle}
              type="password"
              placeholder="Min. 6 characters"
              {...register("password", {
                required: "Password is required",
                minLength: { value: 6, message: "At least 6 characters" },
              })}
              isInvalid={!!errors.password}
            />
            <Form.Control.Feedback type="invalid">
              {errors.password?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Confirm Password <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              style={fieldStyle}
              type="password"
              placeholder="Re-enter password"
              {...register("confirmPassword", {
                required: "Please confirm password",
                validate: (v) =>
                  v === watch("password") || "Passwords do not match",
              })}
              isInvalid={!!errors.confirmPassword}
            />
            <Form.Control.Feedback type="invalid">
              {errors.confirmPassword?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <SectionHeader icon={<ImageFill size={13} />} label="Profile Image" />

          <Form.Group className="mb-4">
            <Form.Control
              style={fieldStyle}
              type="file"
              accept="image/*"
              {...register("files")}
            />
          </Form.Group>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
            style={{
              background: "linear-gradient(135deg, #1a237e, #0d47a1)",
              border: "none",
              borderRadius: "10px",
              padding: "11px",
              boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" animation="border" /> Adding…
              </>
            ) : (
              <>
                <PersonPlusFill size={15} /> Add User
              </>
            )}
          </Button>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddUserOffcanvas;
